/**
 * CodeRain — eerie, slow, cyan glyph rain.
 *
 * Canvas-based. Sparse columns of hex/Cyrillic/runic glyphs drift down.
 * Slower and dimmer than classic Matrix to avoid the cliché. Skips render
 * entirely if prefers-reduced-motion is set.
 */
import React, { useEffect, useRef } from "react";

const GLYPHS =
  "01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモ" +
  "ABCDEF0123456789ᚠᚢᚦᚨᚱᚲᚷᚹᚺᚾᛁᛃᛇᛈᛉᛊᛏᛒᛖᛗᛚᛜᛟᛞ";

export default function CodeRain({
  density = 0.6, // 0-1 columns per 20px
  speed = 0.7,   // px per frame
  fade = 0.07,   // background overlay alpha each frame
  color = "rgba(0, 224, 255, 1)",
  trailColor = "rgba(0, 224, 255, 0.16)",
  fontSize = 14,
  className = "",
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const prefersReduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf = 0;
    let cols = 0;
    let drops = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      cols = Math.floor((canvas.offsetWidth / fontSize) * density);
      drops = new Array(cols).fill(0).map(() => ({
        y: Math.random() * -canvas.offsetHeight,
        x: Math.random() * canvas.offsetWidth,
        speed: speed * (0.5 + Math.random()),
        bright: Math.random() < 0.15,
      }));
    };

    const draw = () => {
      // Trail fade
      ctx.fillStyle = `rgba(2, 4, 10, ${fade})`;
      ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      ctx.font = `${fontSize}px "JetBrains Mono", monospace`;

      drops.forEach((d) => {
        const g = GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
        if (d.bright) {
          ctx.fillStyle = color;
          ctx.shadowBlur = 8;
          ctx.shadowColor = color;
        } else {
          ctx.fillStyle = trailColor;
          ctx.shadowBlur = 0;
        }
        ctx.fillText(g, d.x, d.y);
        d.y += d.speed;
        if (d.y > canvas.offsetHeight + 40) {
          d.y = -20;
          d.x = Math.random() * canvas.offsetWidth;
          d.speed = speed * (0.5 + Math.random());
          d.bright = Math.random() < 0.15;
        }
      });
      ctx.shadowBlur = 0;

      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(raf);
    };
  }, [density, speed, fade, color, trailColor, fontSize]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      data-testid="code-rain"
      aria-hidden="true"
    />
  );
}
